/**
 * useSplitRules — Story 2.3-UI (Split-Editor, Referenz splits.html)
 *
 * TanStack-Query-Hooks fuer die Welle-2-Endpunkte /api/v2/host_split_rules
 * (Liste, Detail, Create, Update) sowie die Stammdaten-Selects des Editors
 * (Hosts aus Story 2.1, Hotspots aus /api/v2/hotspots).
 *
 * Alle Typen kommen aus dem generierten Contract (@heliumtracker/api-types) —
 * es werden keine Felder erfunden.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { components, operations } from '@heliumtracker/api-types'
import apiClient from '../../lib/api-client'
import { useApi } from '../../lib/use-api'

export type HostSplitRule = components['schemas']['HostSplitRule']
export type Host = components['schemas']['Host']

export type SplitRulePayload =
  operations['createApiV2HostSplitRule']['requestBody']['content']['application/json']['host_split_rule']
export type SplitRuleUpdatePayload =
  operations['updateApiV2HostSplitRule']['requestBody']['content']['application/json']['host_split_rule']

export const SPLIT_RULES_QUERY_KEY = ['split-rules'] as const

// Selects/Liste laden bis zu 100 Eintraege (Contract-Maximum per_page=100).
// Fleets jenseits von 100 Hosts/Hotspots brauchen spaeter eine Suche/Pagination.
const MAX_PER_PAGE = 100

// --- Regel-Liste -----------------------------------------------------------

export function useSplitRules() {
  return useApi<HostSplitRule[]>([...SPLIT_RULES_QUERY_KEY], async () => {
    const res = await apiClient.GET('/api/v2/host_split_rules', {
      params: { query: { per_page: MAX_PER_PAGE } },
    })
    if (res.error) return { error: res.error }
    return { data: res.data?.data ?? [] }
  })
}

// --- Regel-Detail ----------------------------------------------------------

export function useSplitRule(id: number | null) {
  return useApi<HostSplitRule>(
    ['split-rule', id],
    async () => {
      const res = await apiClient.GET('/api/v2/host_split_rules/{id}', {
        params: { path: { id: id as number } },
      })
      if (res.error) return { error: res.error }
      return { data: res.data?.data }
    },
    { enabled: id != null && Number.isFinite(id) },
  )
}

// --- Regeln eines einzelnen Hotspots ---------------------------------------

/**
 * heliumtracker-web#24: Provisions-Regeln gefiltert nach Hotspot, fuer die
 * HotspotSplitsCard im Hotspot-Detail. Nutzt den hotspot_id-Query-Param des
 * Listen-Endpunkts statt clientseitig zu filtern.
 */
export function useHotspotSplitRules(hotspotId: number | string | null | undefined) {
  const id = hotspotId != null ? Number(hotspotId) : null
  return useApi<HostSplitRule[]>(
    ['split-rules', 'hotspot', id],
    async () => {
      const res = await apiClient.GET('/api/v2/host_split_rules', {
        params: { query: { per_page: MAX_PER_PAGE, hotspot_id: id as number } },
      })
      if (res.error) return { error: res.error }
      return { data: res.data?.data ?? [] }
    },
    { enabled: id != null && Number.isFinite(id) },
  )
}

// --- Stammdaten fuer die Editor-Selects ------------------------------------

export function useHosts() {
  return useApi<Host[]>(['hosts', 'options'], async () => {
    const res = await apiClient.GET('/api/v2/hosts', {
      params: { query: { per_page: MAX_PER_PAGE } },
    })
    if (res.error) return { error: res.error }
    return { data: res.data?.data ?? [] }
  })
}

export type HotspotOption = { id: number; name: string }

/**
 * Issue #52: der Regel-Editor muss ALLE eigenen Hotspots zur Auswahl
 * anbieten koennen, nicht nur owned+active (sonst fehlen inaktive/
 * quotierte Hotspots, auf die bereits Regeln laufen — sie waren weder
 * sichtbar noch waehlbar). `owned_only=true` liefert deshalb den vollen
 * Bestand des Users (Contract-Param, siehe openapi.yaml).
 */
export function useHotspotOptions() {
  return useApi<HotspotOption[]>(['hotspots', 'split-options'], async () => {
    const res = await apiClient.GET('/api/v2/hotspots', {
      params: {
        query: { per_page: MAX_PER_PAGE, sort_by: 'name', sort_dir: 'asc', owned_only: true },
      },
    })
    if (res.error) return { error: res.error }
    const options = (res.data?.data ?? [])
      .map((h) => ({ id: Number(h.id), name: h.name ?? String(h.id ?? '') }))
      .filter((h) => Number.isFinite(h.id))
    return { data: options }
  })
}

// --- Mutationen ------------------------------------------------------------

export function useCreateSplitRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: SplitRulePayload) => {
      const res = await apiClient.POST('/api/v2/host_split_rules', {
        body: { host_split_rule: payload },
      })
      if (res.error) throw res.error
      return res.data?.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SPLIT_RULES_QUERY_KEY })
    },
  })
}

export function useUpdateSplitRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: SplitRuleUpdatePayload }) => {
      const res = await apiClient.PATCH('/api/v2/host_split_rules/{id}', {
        params: { path: { id } },
        body: { host_split_rule: payload },
      })
      if (res.error) throw res.error
      return res.data?.data
    },
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: SPLIT_RULES_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['split-rule', id] })
    },
  })
}
